import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import SEO, { productLd } from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ProductTile from "@/components/store/ProductTile";
import { toggleCompareId, useCompareIds } from "@/components/store/CompareTray";
import {
  Search,
  Store as StoreIcon,
  Package,
  ShieldCheck,
  Zap,
  ArrowUpRight,
  Filter as FilterIcon,
  MapPin,
  Building2,
  Sparkles,
  Send,
  Check,
  Wrench,
  Scale,
} from "lucide-react";

/**
 * Mirrorloops Store — programmable physical-media commerce discovery.
 *
 * Not a traditional cart. Three routes:
 *   /store                — hero + categories + featured + compat + recent
 *   /store/search         — filtered results grid (accepts every filter as ?q= param)
 *   /store/product/:pid   — detail + offers + procurement links + quote request
 *
 * Category and brand landings reuse the search view with pre-set params.
 */

const COMPAT_TONE = {
  electric: "text-[#CCFF00] border-[#CCFF00]/40 bg-[#CCFF00]/[0.08]",
  cyan: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/[0.06]",
  gold: "text-[#E8D6A3] border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.06]",
  muted: "text-white/50 border-white/15 bg-white/[0.03]",
};

const COUNTRIES = ["Global", "IN", "US", "GB", "AE", "AU", "SG"];

function CompatBadge({ meta, size = "sm" }) {
  const cls = COMPAT_TONE[meta?.tone || "gold"];
  const px = size === "lg" ? "px-3 py-1 text-[11px]" : "px-2 py-0.5 text-[9px]";
  return (
    <span
      data-testid="store-compat-badge"
      className={`inline-flex items-center gap-1 rounded-full border font-mono uppercase tracking-[0.16em] ${px} ${cls}`}
    >
      <ShieldCheck size={size === "lg" ? 12 : 10} />
      {meta?.label || "—"}
    </span>
  );
}

function ProductCard({ p }) {
  const compareIds = useCompareIds();
  const inCompare = compareIds.includes(p.id);
  return (
    <div className="group border border-white/10 rounded-lg bg-white/[0.02] p-4 flex flex-col hover:border-[#D4B878]/50 transition-all relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const added = toggleCompareId(p.id);
          if (!added && !inCompare) {
            // capped at 4
            // eslint-disable-next-line no-alert
          }
        }}
        data-testid={`store-compare-toggle-${p.id}`}
        className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center border transition ${
          inCompare
            ? "bg-[#D4B878] text-black border-[#D4B878]"
            : "border-white/15 text-white/50 bg-black/30 hover:border-[#D4B878] hover:text-[#D4B878]"
        }`}
        title={inCompare ? "Remove from compare" : "Add to compare"}
      >
        <Scale size={13} />
      </button>
      <Link
        to={`/store/product/${p.id}`}
        data-testid={`store-product-card-${p.id}`}
        className="flex flex-col flex-1"
      >
      <div className="flex items-start justify-between gap-2 mb-3 pr-9">
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 font-semibold">
          {p.department}
        </div>
        <CompatBadge meta={p.compat_meta} />
      </div>
      <div className="aspect-video mb-3">
        <ProductTile p={p} size="md" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4B878] font-semibold mb-1">
        {p.manufacturer}
      </div>
      <div className="text-sm font-medium text-white leading-tight mb-2 line-clamp-2">
        {p.name}
      </div>
      <div className="mt-auto flex flex-wrap gap-1.5 text-[9px]">
        {p.size_class && (
          <span className="px-1.5 py-0.5 bg-white/[0.04] border border-white/10 rounded uppercase tracking-[0.14em] text-white/60 font-mono">
            {p.size_class}
          </span>
        )}
        {p.environment && (
          <span className="px-1.5 py-0.5 bg-white/[0.04] border border-white/10 rounded uppercase tracking-[0.14em] text-white/60 font-mono">
            {p.environment}
          </span>
        )}
        {p.commercial_tier && (
          <span className="px-1.5 py-0.5 bg-white/[0.04] border border-white/10 rounded uppercase tracking-[0.14em] text-white/60 font-mono">
            {p.commercial_tier}
          </span>
        )}
      </div>
      </Link>
    </div>
  );
}

// -----------------------------------------------------------------
// /store — homepage
// -----------------------------------------------------------------
export function StoreHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const [country, setCountry] = useState(
    () => localStorage.getItem("mirrorloops_store_country") || "Global",
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/store/homepage");
        setData(r.data);
      } catch {
        setError("Couldn't load the Store.");
      }
    })();
  }, []);

  useEffect(() => {
    localStorage.setItem("mirrorloops_store_country", country);
  }, [country]);

  const goSearch = (extra = {}) => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (country && country !== "Global") params.set("country", country);
    Object.entries(extra).forEach(([k, v]) => v && params.set(k, v));
    nav(`/store/search?${params.toString()}`);
  };

  return (
    <PublicShell>
      <SEO
        title="Store — 4,000+ SKUs for Programmable Physical Media"
        description="Displays, LED walls, kiosks, media players, sensors, mounts and more — curated across 20 departments and 36 manufacturers. Compare side-by-side, build a BOM, request quotes in bulk."
        path="/store"
      />
      <div data-testid="store-home" className="max-w-7xl mx-auto px-6 pt-32 pb-24">
        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-4">
            <StoreIcon size={12} /> Store · Global product discovery
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
            Everything for the
            <br />
            <span className="text-[#D4B878]">Physical Internet.</span>
          </h1>
          <p className="mt-6 text-white/60 text-base max-w-2xl leading-relaxed">
            Discover displays, signage, retail electronics, visual-merchandising
            hardware and infrastructure compatible with modern physical
            commerce. {data ? <span className="text-[#E8D6A3] font-mono">{data.total_products.toLocaleString()}</span> : "—"} products across{" "}
            {data ? data.departments.length : "—"} departments.
          </p>
        </div>

        {/* Universal search + country picker */}
        <div
          data-testid="store-universal-search"
          className="mb-8 flex flex-col md:flex-row gap-3 items-stretch"
        >
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="store-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && goSearch()}
              placeholder="What are you looking for? e.g. Samsung commercial display, high-brightness window signage…"
              className="pl-11 h-14 text-base bg-white/[0.03] border-white/10 focus:border-[#D4B878] text-white placeholder:text-white/30 rounded-full"
            />
          </div>
          <div className="flex items-center gap-2 px-4 border border-white/10 bg-white/[0.02] rounded-full">
            <MapPin size={14} className="text-white/40" />
            <select
              data-testid="store-country-picker"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="bg-transparent text-sm text-white/80 font-mono py-3 focus:outline-none"
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c} className="bg-black">
                  {c}
                </option>
              ))}
            </select>
          </div>
          <Button
            onClick={() => goSearch()}
            data-testid="store-search-submit"
            className="h-14 rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold px-8"
          >
            Find Products →
          </Button>
        </div>

        {/* BOM + Bundles callout */}
        <div className="mb-14 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link
            to="/store/build"
            data-testid="store-cta-bom"
            className="group border border-[#CCFF00]/25 bg-gradient-to-br from-[#CCFF00]/[0.05] to-transparent rounded-lg p-5 flex items-start gap-4 hover:border-[#CCFF00]/50 transition-all"
          >
            <div className="w-12 h-12 rounded-full bg-[#CCFF00]/15 flex items-center justify-center shrink-0">
              <Wrench size={22} className="text-[#CCFF00]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] font-semibold mb-1">
                Build My Store · 5-question wizard
              </div>
              <div className="text-sm text-white font-medium">
                Get a proposed Bill of Materials in under a minute →
              </div>
              <div className="text-xs text-white/50 mt-1">
                Curated across storefront, interior, commerce and infrastructure — sized to your area and budget.
              </div>
            </div>
            <ArrowUpRight size={18} className="text-[#CCFF00]/50 group-hover:text-[#CCFF00] transition-colors mt-1" />
          </Link>
          <Link
            to="/store/bundles"
            data-testid="store-cta-bundles"
            className="group border border-[#D4B878]/25 bg-gradient-to-br from-[#D4B878]/[0.05] to-transparent rounded-lg p-5 flex items-start gap-4 hover:border-[#D4B878]/50 transition-all"
          >
            <div className="w-12 h-12 rounded-full bg-[#D4B878]/15 flex items-center justify-center shrink-0">
              <Package size={22} className="text-[#D4B878]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-1">
                Solution Bundles · pre-built kits
              </div>
              <div className="text-sm text-white font-medium">
                Ready-to-deploy kits: Coffee Shop, Restaurant, Boutique →
              </div>
              <div className="text-xs text-white/50 mt-1">
                Hand-picked SKUs, tuned tier, quotable in one click.
              </div>
            </div>
            <ArrowUpRight size={18} className="text-[#D4B878]/50 group-hover:text-[#D4B878] transition-colors mt-1" />
          </Link>
        </div>

        {error && <div className="text-red-400 text-sm mb-6">{error}</div>}

        {/* Department cards */}
        {data && (
          <section className="mb-16" data-testid="store-departments">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-4">
              <Sparkles size={12} /> Shop by category
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {data.departments.slice(0, 20).map((d) => (
                <button
                  key={d.slug}
                  onClick={() => nav(`/store/search?department=${d.slug}`)}
                  data-testid={`store-dept-${d.slug}`}
                  className="text-left border border-white/10 bg-white/[0.02] hover:border-[#D4B878]/40 hover:bg-[#D4B878]/[0.03] transition-all rounded-lg p-4 aspect-square flex flex-col justify-between"
                >
                  <Package size={18} className="text-[#D4B878]/60" />
                  <div>
                    <div className="text-sm font-semibold text-white leading-tight mb-1">
                      {d.department}
                    </div>
                    <div className="text-[10px] font-mono text-white/40">
                      {d.count} products
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Mirrorloops-compatible showcase */}
        {data && data.mirrorloops_compat_showcase?.length > 0 && (
          <section className="mb-16" data-testid="store-compat-showcase">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] font-semibold mb-4">
              <Zap size={12} /> Mirrorloops-compatible hardware
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.mirrorloops_compat_showcase.map((p) => (
                <ProductCard key={p.id} p={p} />
              ))}
            </div>
          </section>
        )}

        {/* Featured brands */}
        {data && data.featured_brands?.length > 0 && (
          <section className="mb-16" data-testid="store-brands">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#E8D6A3] font-semibold mb-4">
              <Building2 size={12} /> Shop by brand
            </div>
            <div className="flex flex-wrap gap-2">
              {data.featured_brands.slice(0, 12).map((b) => (
                <button
                  key={b.slug}
                  data-testid={`store-brand-${b.slug}`}
                  onClick={() => nav(`/store/search?manufacturer=${b.slug}`)}
                  className="px-4 py-2 border border-white/15 rounded-full text-sm text-white/80 hover:border-[#D4B878] hover:text-white transition"
                >
                  {b.manufacturer}{" "}
                  <span className="ml-1 text-[10px] font-mono text-white/40">
                    {b.count}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Recently added */}
        {data && data.recently_added?.length > 0 && (
          <section className="mb-16" data-testid="store-recent">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-4">
              <Sparkles size={12} /> Recently added
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.recently_added.map((p) => (
                <ProductCard key={p.id} p={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </PublicShell>
  );
}

// -----------------------------------------------------------------
// /store/search — filtered results
// -----------------------------------------------------------------
export function StoreSearch() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const filters = useMemo(
    () => ({
      q: params.get("q") || "",
      department: params.get("department") || "",
      category: params.get("category") || "",
      manufacturer: params.get("manufacturer") || "",
      compat: params.get("compat") || "",
      size_class: params.get("size_class") || "",
      environment: params.get("environment") || "",
      tier: params.get("tier") || "",
      country: params.get("country") || "",
      sort: params.get("sort") || "name",
      page: parseInt(params.get("page") || "1", 10),
    }),
    [params],
  );

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get("/store/categories"),
          api.get("/store/manufacturers"),
        ]);
        setCategories(c.data.tree || []);
        setManufacturers(m.data.items || []);
      } catch {
        // silent — filters just show blank
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== 0) qs.set(k, v);
    });
    qs.set("limit", "24");
    api
      .get(`/store/products?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  const clearAll = () => {
    setParams(new URLSearchParams());
  };

  const activeCount = Object.entries(filters).filter(
    ([k, v]) => v && !["page", "sort"].includes(k),
  ).length;

  return (
    <PublicShell>
      <div data-testid="store-search-page" className="max-w-7xl mx-auto px-6 pt-32 pb-24">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-2">
            Store · Search
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-white">
            {filters.q ? (
              <>
                Results for{" "}
                <span className="text-[#D4B878]">"{filters.q}"</span>
              </>
            ) : filters.department ? (
              <>Browsing {filters.department}</>
            ) : filters.manufacturer ? (
              <>Products by {filters.manufacturer}</>
            ) : (
              "All products"
            )}
          </h1>
          <p className="text-white/50 text-sm mt-2">
            {data ? `${data.total.toLocaleString()} matching products` : "Loading…"}
            {activeCount > 0 && (
              <button
                onClick={clearAll}
                data-testid="store-clear-filters"
                className="ml-3 text-[#D4B878] underline decoration-dotted underline-offset-2 text-xs"
              >
                Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
              </button>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
          {/* Filters */}
          <aside data-testid="store-filters" className="space-y-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 flex items-center gap-1">
                <FilterIcon size={11} /> Search
              </div>
              <Input
                value={filters.q}
                onChange={(e) => setFilter("q", e.target.value)}
                placeholder="e.g. display, kiosk…"
                data-testid="store-filter-q"
                className="h-9 bg-white/5 border-white/10 text-white text-xs"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                Compatibility
              </div>
              {[
                { k: "", label: "All" },
                { k: "compatible", label: "Native + Compatible" },
                { k: "evaluate", label: "Evaluate / Integrate" },
              ].map((c) => (
                <button
                  key={c.k || "all"}
                  data-testid={`store-filter-compat-${c.k || "all"}`}
                  onClick={() => setFilter("compat", c.k)}
                  className={`block w-full text-left text-xs px-2 py-1 rounded ${
                    filters.compat === c.k
                      ? "bg-[#D4B878]/15 text-[#E8D6A3]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                Department
              </div>
              <div className="max-h-52 overflow-y-auto pr-1 space-y-0.5">
                <button
                  data-testid="store-filter-dept-all"
                  onClick={() => setFilter("department", "")}
                  className={`block w-full text-left text-xs px-2 py-1 rounded ${
                    !filters.department
                      ? "bg-[#D4B878]/15 text-[#E8D6A3]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  All departments
                </button>
                {categories.map((d) => (
                  <button
                    key={d.slug}
                    data-testid={`store-filter-dept-${d.slug}`}
                    onClick={() => setFilter("department", d.slug)}
                    className={`block w-full text-left text-xs px-2 py-1 rounded ${
                      filters.department === d.slug
                        ? "bg-[#D4B878]/15 text-[#E8D6A3]"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    {d.department}{" "}
                    <span className="text-white/30 font-mono">({d.total})</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                Environment
              </div>
              {["Indoor", "Indoor 24/7", "Outdoor", "Semi-outdoor", "Window-facing"].map(
                (e) => (
                  <button
                    key={e}
                    data-testid={`store-filter-env-${e.replace(/\s+/g, "-")}`}
                    onClick={() => setFilter("environment", filters.environment === e ? "" : e)}
                    className={`block w-full text-left text-xs px-2 py-1 rounded ${
                      filters.environment === e
                        ? "bg-[#00E5FF]/15 text-[#00E5FF]"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    {e}
                  </button>
                ),
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                Tier
              </div>
              {["Budget", "Mainstream", "Professional", "Enterprise"].map((t) => (
                <button
                  key={t}
                  data-testid={`store-filter-tier-${t}`}
                  onClick={() => setFilter("tier", filters.tier === t ? "" : t)}
                  className={`block w-full text-left text-xs px-2 py-1 rounded ${
                    filters.tier === t
                      ? "bg-[#CCFF00]/15 text-[#CCFF00]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </aside>

          {/* Results */}
          <div>
            {loading && !data && (
              <div className="text-white/40 text-sm">Searching…</div>
            )}
            {data && data.items.length === 0 && (
              <div
                data-testid="store-empty"
                className="p-10 border border-dashed border-white/10 text-center text-white/40 rounded"
              >
                No products match these filters.
              </div>
            )}
            {data && data.items.length > 0 && (
              <>
                <div
                  data-testid="store-results-grid"
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
                >
                  {data.items.map((p) => (
                    <ProductCard key={p.id} p={p} />
                  ))}
                </div>
                {data.pages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2 text-xs text-white/50">
                    {Array.from({ length: Math.min(data.pages, 10) }, (_, i) => i + 1).map(
                      (n) => (
                        <button
                          key={n}
                          data-testid={`store-page-${n}`}
                          onClick={() => setFilter("page", n)}
                          className={`w-8 h-8 rounded font-mono ${
                            filters.page === n
                              ? "bg-[#D4B878] text-black"
                              : "border border-white/10 hover:border-white/30"
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                    {data.pages > 10 && (
                      <span className="text-white/30">… {data.pages}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </PublicShell>
  );
}

// -----------------------------------------------------------------
// /store/product/:pid — product detail + quote request
// -----------------------------------------------------------------
export function StoreProduct() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    quantity: 1,
    deployment_type: "",
    installation_required: false,
    timeline: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/store/products/${pid}`);
        setData(r.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "Product not found."
            : "Couldn't load product.",
        );
      }
    })();
  }, [pid]);

  const submitQuote = async () => {
    if (!quote.name || !quote.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/store/quote-requests", {
        product_id: pid,
        ...quote,
      });
      setSubmitted(true);
      toast.success(`Quote request logged · ID ${r.data.quote_id.slice(0, 8)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/60">
          <div className="text-lg font-semibold text-red-300 mb-4">{error}</div>
          <Link to="/store" className="text-[#D4B878] underline">
            ← Back to Store
          </Link>
        </div>
      </PublicShell>
    );
  }

  if (!data) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/40">
          Loading…
        </div>
      </PublicShell>
    );
  }

  const p = data.product;

  return (
    <PublicShell>
      <SEO
        title={`${p.name} — ${p.manufacturer}`}
        description={`${p.recommendation_basis || p.name}. ${p.mirrorloops_use_case || ""}`.slice(0, 240)}
        path={`/store/product/${p.id}`}
        type="product"
        image={p.image_url || undefined}
        jsonLd={productLd(p)}
      />
      <div
        data-testid={`store-product-page-${p.id}`}
        className="max-w-6xl mx-auto px-6 pt-32 pb-24"
      >
        {/* Breadcrumb */}
        <div className="text-[11px] text-white/40 mb-4 flex items-center gap-2 flex-wrap font-mono">
          <Link to="/store" className="hover:text-white">Store</Link>
          <span>/</span>
          <Link
            to={`/store/search?department=${p.department_slug}`}
            className="hover:text-white"
          >
            {p.department}
          </Link>
          <span>/</span>
          <Link
            to={`/store/search?category=${p.category_slug}`}
            className="hover:text-white"
          >
            {p.category}
          </Link>
          <span>/</span>
          <span className="text-white/70">{p.id}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
          {/* Left: imagery + description */}
          <div>
            <div className="aspect-video mb-6">
              <ProductTile p={p} size="lg" />
            </div>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-1">
                  {p.manufacturer}
                </div>
                <h1 className="text-3xl font-semibold text-white leading-tight">
                  {p.name}
                </h1>
              </div>
              <CompatBadge meta={p.compat_meta} size="lg" />
            </div>
            <div className="text-sm text-white/60 mb-6 flex flex-wrap gap-3 font-mono">
              <span>ID: {p.id}</span>
              <span>·</span>
              <span>{p.category}</span>
              <span>·</span>
              <span>{p.product_type}</span>
            </div>
            {p.recommendation_basis && (
              <div className="p-3 border border-white/10 bg-white/[0.02] rounded text-[13px] text-white/70 leading-relaxed mb-6">
                {p.recommendation_basis}
              </div>
            )}
            {p.mirrorloops_use_case && (
              <div
                data-testid="store-product-use-case"
                className="p-4 border border-[#CCFF00]/25 bg-[#CCFF00]/[0.04] rounded mb-6"
              >
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] font-semibold mb-2">
                  Mirrorloops use case
                </div>
                <div className="text-[13px] text-white/85">
                  {p.mirrorloops_use_case}
                </div>
                {p.player_required && p.player_required !== "" && (
                  <div className="mt-2 text-[11px] font-mono text-white/50">
                    Player required: {p.player_required}
                  </div>
                )}
              </div>
            )}
            {/* Specs */}
            <div className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-3">
                Specification checkpoints
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px]">
                {[
                  ["Size class", p.size_class],
                  ["Environment", p.environment],
                  ["Tier", p.commercial_tier],
                  ["Industry", p.industry],
                  ["Availability", p.availability_region],
                  ["Installation", p.installation_complexity],
                  ["Procurement", p.procurement_mode],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between border-b border-white/5 py-1.5"
                  >
                    <span className="text-white/40 uppercase text-[10px] tracking-[0.18em]">
                      {k}
                    </span>
                    <span className="text-white/85 font-mono text-[12px]">
                      {v}
                    </span>
                  </div>
                ))}
              </div>
              {p.key_specs?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">
                    Verify before purchase
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.key_specs.map((s) => (
                      <span
                        key={s}
                        className="px-2 py-0.5 bg-white/[0.03] border border-white/10 rounded-full text-[10px] uppercase tracking-[0.14em] text-white/60 font-mono"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Related */}
            {data.related?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-3">
                  Related products
                </div>
                <div
                  data-testid="store-product-related"
                  className="grid grid-cols-2 md:grid-cols-3 gap-3"
                >
                  {data.related.slice(0, 6).map((r) => (
                    <ProductCard key={r.id} p={r} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: procurement panel */}
          <aside className="space-y-4">
            <div
              data-testid="store-procurement-panel"
              className="border border-white/10 rounded-lg bg-white/[0.02] p-5 sticky top-24"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-3">
                Find this product
              </div>
              {data.offers?.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {data.offers.map((o) => (
                    <a
                      key={o.id}
                      href={o.seller_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`store-offer-${o.id}`}
                      className="block border border-white/10 rounded p-3 hover:border-[#D4B878]/40 transition"
                    >
                      <div className="text-sm font-semibold text-white">
                        {o.seller}
                      </div>
                      <div className="text-[10px] text-white/50 font-mono">
                        {o.seller_country} · {o.currency}
                        {o.price ? ` · ${o.price}` : ""}
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-white/50 mb-3 italic">
                  No cached seller offers. Use the marketplace search links below.
                </div>
              )}
              <div className="space-y-1.5 mb-4">
                {data.procurement_links.map((l) => (
                  <a
                    key={l.source}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`store-procurement-${l.source.replace(/\s+/g, "-")}`}
                    className="flex items-center justify-between text-[12px] text-white/80 hover:text-white border border-white/10 hover:border-[#D4B878]/40 rounded px-2.5 py-1.5 transition"
                  >
                    <span className="flex items-center gap-2">
                      <ArrowUpRight size={11} className="text-[#D4B878]" />
                      Check on {l.source}
                    </span>
                    <span className="text-[9px] font-mono text-white/40">{l.region}</span>
                  </a>
                ))}
              </div>
              <Button
                onClick={() => setQuoteOpen(true)}
                data-testid="store-quote-open"
                className="w-full rounded-full bg-[#CCFF00] hover:bg-[#E8FF66] text-black font-semibold"
              >
                <Send size={14} className="mr-2" /> Request a quote
              </Button>
              <div className="mt-3 text-[10px] text-white/40 leading-relaxed">
                For enterprise pricing, bulk orders or installations, our
                procurement team responds within 1 business day.
              </div>
            </div>
          </aside>
        </div>

        {/* Quote dialog */}
        {quoteOpen && (
          <div
            data-testid="store-quote-dialog"
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => !submitting && setQuoteOpen(false)}
          >
            <div
              className="bg-[#141110] border border-white/15 rounded-lg max-w-xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-1">
                    Request quote
                  </div>
                  <div className="text-lg font-semibold text-white">{p.name}</div>
                  <div className="text-[11px] font-mono text-white/50">{p.id}</div>
                </div>
                <button
                  onClick={() => setQuoteOpen(false)}
                  className="text-white/40 hover:text-white"
                  data-testid="store-quote-close"
                >
                  ✕
                </button>
              </div>
              {submitted ? (
                <div
                  data-testid="store-quote-submitted"
                  className="p-6 text-center border border-[#CCFF00]/40 bg-[#CCFF00]/[0.06] rounded"
                >
                  <Check size={32} className="mx-auto text-[#CCFF00] mb-3" />
                  <div className="text-white font-semibold mb-1">
                    Quote request received.
                  </div>
                  <div className="text-[12px] text-white/60">
                    Our procurement team will respond within 1 business day.
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Name</Label>
                      <Input
                        data-testid="store-quote-name"
                        value={quote.name}
                        onChange={(e) => setQuote({ ...quote, name: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Email</Label>
                      <Input
                        data-testid="store-quote-email"
                        type="email"
                        value={quote.email}
                        onChange={(e) => setQuote({ ...quote, email: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Company (optional)</Label>
                      <Input
                        data-testid="store-quote-company"
                        value={quote.company}
                        onChange={(e) => setQuote({ ...quote, company: e.target.value })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Country</Label>
                      <Input
                        data-testid="store-quote-country"
                        value={quote.country}
                        onChange={(e) => setQuote({ ...quote, country: e.target.value })}
                        placeholder="e.g. India"
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Quantity</Label>
                      <Input
                        data-testid="store-quote-quantity"
                        type="number"
                        min={1}
                        value={quote.quantity}
                        onChange={(e) => setQuote({ ...quote, quantity: parseInt(e.target.value || "1", 10) })}
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Timeline</Label>
                      <Input
                        data-testid="store-quote-timeline"
                        value={quote.timeline}
                        onChange={(e) => setQuote({ ...quote, timeline: e.target.value })}
                        placeholder="e.g. within 6 weeks"
                        className="bg-white/5 border-white/10 mt-1"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-[12px] text-white/70">
                    <input
                      type="checkbox"
                      data-testid="store-quote-install"
                      checked={quote.installation_required}
                      onChange={(e) => setQuote({ ...quote, installation_required: e.target.checked })}
                    />
                    Installation required
                  </label>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Notes</Label>
                    <textarea
                      data-testid="store-quote-notes"
                      value={quote.notes}
                      onChange={(e) => setQuote({ ...quote, notes: e.target.value })}
                      placeholder="Deployment context, venue type, etc."
                      className="w-full bg-white/5 border border-white/10 mt-1 rounded p-2 text-sm text-white h-20"
                    />
                  </div>
                  <Button
                    onClick={submitQuote}
                    disabled={submitting || !quote.name || !quote.email}
                    data-testid="store-quote-submit"
                    className="w-full rounded-full bg-[#CCFF00] hover:bg-[#E8FF66] text-black font-semibold disabled:opacity-40"
                  >
                    {submitting ? "Submitting…" : "Send request"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
